import { Moment } from 'moment';
import Stripe from 'stripe';
import { Auditable } from './Auditable';
import { Serialized } from './typeutil';

export type PlanInterval = 'year' | 'month' | 'day';

export const ACCOUNT_TYPE_USER = 'user';
export const ACCOUNT_TYPE_ORG = 'org';

export const ACCOUNT_TYPES = [ACCOUNT_TYPE_USER, ACCOUNT_TYPE_ORG] as const;

/**
 * For differentiating requests between a user or org
 */
export type AccountType = (typeof ACCOUNT_TYPES)[number];

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const STRIPE_SUBSCRIPTION_STATUS = [
  'active',
  'trialing',
  'past_due',
  'canceled',
  'unpaid',
  'incomplete',
  'incomplete_expired',
] as const;

export type StripeSubscriptionStatus =
  (typeof STRIPE_SUBSCRIPTION_STATUS)[number];

// ## Account

/**
 * An account.subscription record
 */
export type Subscription = {
  id: string;
  status: StripeSubscriptionStatus;
  created: Moment;
  trial_start?: Moment;
  trial_end?: Moment;
  current_period_start: Moment;
  current_period_end: Moment;
  cancel_at_period_end: boolean;
  plan: string;
  amount: number;
  interval: 'year' | 'month' | 'day';
  interval_count: number;
};

/**
 * An account.card record
 */
export type Card = {
  name: string;
  brand: string;
  last4: string;
  status: 'canceled' | 'chargeable' | 'consumed' | 'failed' | 'pending';
  is_chargeable: boolean;
};

/**
 * An account.payment record
 */
export type Payment = {
  receipt_url: string;
  amount: number;
  created: Moment;
  nickname: string;
  status: 'succeeded' | 'pending' | 'failed';
  refunded: boolean;
  refunds?: {
    amount: number;
    created: Moment;
    status: 'pending' | 'succeeded';
  }[];
};

/**
 * An account.abTestV2 record
 */
export type AbTestV2 = {
  bucket: number;
  created: number;
  group: 'classic' | 'stripe';
  trial_start?: number;
  sub_start?: number;
  no_track?: boolean;
};

/** enterprise offering */
export const MD_PRODUCT_TYPE_ENTERPRISE = 'enterprise';

/** entry-level premium plans, e.g., $12/mo */
export const MD_PRODUCT_TYPE_PREMIUM = 'premium';

export const MD_PRODUCT_TYPES = [
  MD_PRODUCT_TYPE_ENTERPRISE,
  MD_PRODUCT_TYPE_PREMIUM,
] as const;

export type ProductType = (typeof MD_PRODUCT_TYPES)[number];

/**
 * a serialized Product from our API
 * (duped in pricingutil.js)
 */
export type Product = Pick<
  Stripe.Product,
  'id' | 'active' | 'description' | 'name'
> & {
  // currently on stripe@^11 until migrate
  // our API higher, since later versions
  // pin against stripe versions, so manually
  // add missing type values
  marketing_features: {
    name?: string;
  }[];
  productType: ProductType | null;
};

/**
 * a serialized Price with a Product from our API
 * (duped in pricingutil.js)
 */
export type Price = Pick<
  Stripe.Price,
  | 'id'
  | 'active'
  | 'billing_scheme'
  | 'currency'
  | 'lookup_key'
  | 'recurring'
  | 'type'
  | 'unit_amount'
> & {
  product: Product;
};

/**
 * An account record (with Moments instead of timestamp numbers)
 */
export type Account = {
  userId: string;
  email?: string;
  /** designates access to admin UI */
  isStaff?: boolean;
  orgId?: string;
  orgRole?: OrganizationMemberRole;
  orgStatus?: OrganizationMemberStatus;
  orgName?: string;
  orgAdminEmail?: string;
  customerId?: string;
  status?: StripeSubscriptionStatus;
  subscription?: Subscription;
  card?: Card;
  payments?: Payment[];
  abTestV2?: AbTestV2;
  lookupPrice?: Price;
};

/**
 * The Account type returned from the API
 */
export type ApiAccount = Serialized<Account>;

// ## Organization

export type NewOrganization = {
  name: string;
  seats: number;
  adminEmail: string;
  customerId?: string;
  subscription?: Subscription;
  card?: Card;
};

export type ApiOrganization = Serialized<Organization>;

export type Organization = NewOrganization & {
  orgId: string;
} & Auditable;

export type OrganizationMemberStatus =
  'invited' | 'active' | 'deactivated' | 'archived';

export enum OrganizationMemberStatuses {
  Invited = 'invited',
  Active = 'active',
  Deactivated = 'deactivated',
  Archived = 'archived',
}

export type OrganizationMemberRole = 'admin' | 'user';

export enum OrganizationMemberRoles {
  Admin = 'admin',
  User = 'user',
}

export type OrganizationMember = Pick<Account, 'userId' | 'email'> & {
  orgId: string;
  orgRole: OrganizationMemberRole;
  orgStatus: OrganizationMemberStatus;
} & Auditable;
